if (!customElements.get('wholesale-showcase')) {
  customElements.define('wholesale-showcase', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.pc-wholesale-products');
      this.cards = [...this.querySelectorAll('.pc-wholesale-product')];
      if (this.cards.length < 2) return;
      this.pages = [];
      this.rebuild();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches;
      this.visible = false;
      this.hovered = false;
      this.focused = false;
      this.querySelector('[data-previous]').addEventListener('click', () => this.move(-1, true), options);
      this.querySelector('[data-next]').addEventListener('click', () => this.move(1, true), options);
      this.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') { this.hovered = true; this.schedule(); } }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.paused = true; this.update(); }, options);
      this.addEventListener('focusout', e => { this.focused = this.contains(e.relatedTarget); this.schedule(); }, options);
      this.track.addEventListener('pointerdown', () => { this.paused = true; this.update(); }, options);
      this.track.addEventListener('keydown', e => {
        if (e.key === ' ' && e.target === this.track) {
          e.preventDefault();
          this.paused = !this.paused;
          this.update();
          return;
        }
        if (e.target !== this.track || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
        e.preventDefault();
        this.move(e.key === 'ArrowLeft' ? -1 : 1, true);
      }, options);
      this.track.addEventListener('scroll', () => this.count(), { ...options, passive: true });
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', () => { if (this.motion.matches) this.paused = true; this.update(); }, options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.schedule();
      }, { threshold: 0.25 });
      this.observer.observe(this);
      this.resize = new ResizeObserver(() => { this.rebuild(); this.count(); });
      this.resize.observe(this.track);
      this.update();
    }
    pageSize() { return innerWidth >= 990 ? 8 : 4; }
    rebuild() {
      const size = this.pageSize();
      if (size === this.size) return;
      const first = this.pages.length ? this.index() * this.size : 0;
      const focused = this.contains(document.activeElement) ? document.activeElement : null;
      this.size = size;
      this.pages = [];
      for (let i = 0; i < this.cards.length; i += size) {
        const page = document.createElement('div');
        page.className = 'pc-wholesale-product-page';
        page.append(...this.cards.slice(i, i + size));
        this.pages.push(page);
      }
      this.track.replaceChildren(...this.pages);
      this.track.dataset.paged = '';
      this.querySelector('.pc-wholesale-carousel-controls').hidden = this.pages.length < 2;
      const page = this.pages[Math.min(Math.floor(first / size), this.pages.length - 1)];
      this.track.scrollTo({ left: page.offsetLeft - this.pages[0].offsetLeft, behavior: 'instant' });
      focused?.focus({ preventScroll: true });
    }
    index() {
      return this.pages.reduce((nearest, page, i) => {
        const distance = Math.abs(this.track.scrollLeft - (page.offsetLeft - this.pages[0].offsetLeft));
        const previous = Math.abs(this.track.scrollLeft - (this.pages[nearest].offsetLeft - this.pages[0].offsetLeft));
        return distance < previous ? i : nearest;
      }, 0);
    }
    move(direction, manual = false) {
      if (manual) this.paused = true;
      const next = (this.index() + direction + this.pages.length) % this.pages.length;
      this.track.scrollTo({ left: this.pages[next].offsetLeft - this.pages[0].offsetLeft, behavior: this.motion.matches ? 'instant' : 'smooth' });
      this.update();
    }
    count() {
      const first = this.index() * this.size;
      this.querySelector('[data-count]').textContent = `${first + 1}–${Math.min(first + this.size, this.cards.length)} / ${this.cards.length}`;
    }
    update() {
      this.querySelector('[data-count]').setAttribute('aria-live', this.paused ? 'polite' : 'off');
      this.count();
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      if (this.pages.length < 2 || this.paused || this.hovered || this.focused || !this.visible || document.hidden) return;
      this.timer = setTimeout(() => this.move(1), Number(this.dataset.interval) || 7000);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.abort?.abort();
      this.observer?.disconnect();
      this.resize?.disconnect();
    }
  });
}
